import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  useMediaQuery,
  useTheme,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  LinearProgress,
  Chip,
  Divider,
  Alert
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  FiberManualRecord as CurrentIcon
} from '@mui/icons-material';

import BasicInformationForm   from './BasicInformationForm';
import LocationHoursForm      from './LocationHoursForm';
import CategoriesServicesForm from './CategoriesServicesForm';
import PhotosDetailsForm      from './PhotosDetailsForm';
import UpgradePrompt          from './UpgradePrompt';
import BasicSEO               from '../../Components/SEO/BasicSEO';

import {
  BUSINESS_QUOTAS,
  FORM_STEPS,
  FORM_TIPS,
  DEFAULT_FORM_DATA
} from './BusinessFormConstants';

import { validateLocationHours }         from './validateLocationHours';
import { useThingAddMutation }           from '../../redux/thunks/things/thingadd';
import { usePrefetch as useGetAllSkillsPrefetch } from '../../redux/thunks/things/thingsget';
import { useGetPreSignedUrlQuery }       from '../../redux/thunks/files/filespresignurl';
import { useSpFindBusinessNameMutation } from '../../redux/thunks/sp/spfindbusinessname';
import { useGetUserProfileQuery }        from '../../redux/thunks/users/userprofile';
import { useGetPreSignedUrlAudioQuery }  from '../../redux/thunks/files/filespresignurlaudio';
import { uploadAudioToS3 }               from './BasicInformationForm';

// ===== Validation =====
const validateBasicInformation = (data) => {
  const errors = {};
  if (!data.userRole?.trim())      errors.userRole     = 'Please select your role in the business';
  if (!data.businessType)          errors.businessType = 'Please select business type';
  if (!data.businessName?.trim())  errors.businessName = 'Business name is required';
  if (!data.category)              errors.category     = 'Category is required';
  if (!data.phone?.trim())         errors.phone        = 'Phone number is required';
  if (!data.email?.trim())         errors.email        = 'Email is required';

  // At least 3 words naming what they offer — unless they recorded audio instead.
  const desc  = (data.description || '').trim();
  const words = desc.split(/\s+/).filter(Boolean).length;
  if (!data.audioDescription?.file && words < 3) {
    errors.description = 'Name at least 2–3 things you sell or offer — see the example in the box.';
  }
  return errors;
};

// ===== Compact Sidebar Content =====
const SidebarContent = ({
  userAccountType, currentBusinessCount, businessQuota,
  isUserLoading, currentStep
}) => {
  const canCreateMore   = currentBusinessCount < businessQuota;
  const quotaPercent    = Math.round((currentBusinessCount / businessQuota) * 100);
  const currentTips     = FORM_TIPS[currentStep] || [];

  return (
    <Box>
      {/* Quota */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, border: 'none', bgcolor: 'grey.50', borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Business Quota
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {currentBusinessCount} of {businessQuota} used
          </Typography>
          <Chip
            label={userAccountType}
            size="small"
            color={userAccountType === 'premium' ? 'primary' : 'default'}
            sx={{ textTransform: 'capitalize', height: 20, fontSize: '0.7rem' }}
          />
        </Box>
        <LinearProgress
          variant="determinate"
          value={Math.min(quotaPercent, 100)}
          color={canCreateMore ? 'success' : 'error'}
          sx={{ height: 6, borderRadius: 3 }}
        />
        {!canCreateMore && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
            Quota exceeded — upgrade to add more
          </Typography>
        )}
        {userAccountType === 'free' && canCreateMore && (
          <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 0.5 }}>
            Upgrade to Premium for up to 5 businesses
          </Typography>
        )}
      </Paper>

      {/* Step Progress */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, border: 'none', bgcolor: 'grey.50', borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Progress
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
          {FORM_STEPS.map((step, index) => {
            const isCompleted = index < currentStep;
            const isCurrent   = index === currentStep;
            return (
              <Box
                key={index}
                sx={{
                  display:     'flex',
                  alignItems:  'center',
                  gap:         1,
                  p:           1,
                  borderRadius: 1,
                  bgcolor:     isCurrent ? 'primary.50' : isCompleted ? 'success.50' : 'transparent',
                  border:      '1px solid',
                  borderColor: isCurrent ? 'primary.main' : isCompleted ? 'success.main' : 'transparent'
                }}
              >
                {isCompleted
                  ? <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main', flexShrink: 0 }} />
                  : isCurrent
                    ? <CurrentIcon sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }} />
                    : <UncheckedIcon sx={{ fontSize: 16, color: 'grey.400', flexShrink: 0 }} />
                }
                <Typography
                  variant="body2"
                  fontWeight={isCurrent ? 600 : 400}
                  color={isCurrent ? 'primary.main' : isCompleted ? 'success.main' : 'text.secondary'}
                >
                  {step}
                </Typography>
              </Box>
            );
          })}
        </Box>
        <Box sx={{ mt: 1.5 }}>
          <LinearProgress
            variant="determinate"
            value={((currentStep + 1) / FORM_STEPS.length) * 100}
            sx={{ height: 4, borderRadius: 2 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}>
            Step {currentStep + 1} of {FORM_STEPS.length}
          </Typography>
        </Box>
      </Paper>

      {/* Tips */}
      {currentTips.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, border: 'none', bgcolor: 'info.50', borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            💡 Tips for this step
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {currentTips.map((tip, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Typography variant="caption" color="info.main" fontWeight={700} sx={{ flexShrink: 0, mt: 0.1 }}>
                  {i + 1}.
                </Typography>
                <Typography variant="caption" color="info.dark" sx={{ lineHeight: 1.5 }}>
                  {tip}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

// ===== Mobile Drawer =====
const MobileDrawer = ({ isOpen, onClose, ...sidebarProps }) => (
  <Drawer
    anchor="right"
    open={isOpen}
    onClose={onClose}
    PaperProps={{ sx: { width: '100%', maxWidth: 360, bgcolor: 'background.default' } }}
  >
    <AppBar position="static" color="default" elevation={0}>
      <Toolbar>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Setup Info</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Toolbar>
    </AppBar>
    <Box sx={{ p: 2, overflow: 'auto', height: '100%' }}>
      <SidebarContent {...sidebarProps} />
    </Box>
  </Drawer>
);

// ===== Main Component =====
const AddBusinessPage = () => {
  const navigate  = useNavigate();
  const theme     = useTheme();
  const isMobile  = useMediaQuery(theme.breakpoints.down('md'));

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const token  = useSelector((state) => state.auth?.token  || null);
  const userId = useSelector((state) => state.auth?.userId || null);
  const spId     = useSelector((state) => state.auth?.user?.spId);
  const authUser = useSelector((state) => state.auth?.user || null);

  const args = { eventType: 'BusinessCreatePhotos', userId };

  const { data: presignedUrlData,  isLoading: isPresignedUrlLoading } = useGetPreSignedUrlQuery(args);

  const [
    triggerSearchBusinessName,
    { isLoading: isSimilarBusinessesDataLoading }
  ] = useSpFindBusinessNameMutation();

  const {
    data:     userData,
    isLoading: isUserLoading
  } = useGetUserProfileQuery(userId);

  const userAccountType       = userData?.accountDetails?.type || 'free';
  const businessQuota         = BUSINESS_QUOTAS[userAccountType] || BUSINESS_QUOTAS.free;
  const currentBusinessCount  = userData?.myBusinesses?.length || 0;
  const canCreateBusiness     = currentBusinessCount < businessQuota;

  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading,   setIsLoading]   = useState(false);
  const [errors,      setErrors]      = useState({});
  const [submitError, setSubmitError] = useState('');
  const [formData,    setFormData]    = useState(() => ({
    ...DEFAULT_FORM_DATA,
    email: authUser?.email || ''
  }));

  const {
    data:      audioPresignedData,
    isLoading: isAudioUrlLoading,
    error:     audioUrlError
  } = useGetPreSignedUrlAudioQuery(
    { eventType: 'BusinessCreateAudio', audioType: formData.audioDescription?.type || 'webm' },
    { skip: !formData.audioDescription?.file }
  );

  const prefetchGetAllSkills                       = useGetAllSkillsPrefetch('getAllThings');
  const [addThing, { isLoading: addBusinessLoading }] = useThingAddMutation();

  // ===== SEO =====
  const stepNames    = ['Basic Information', 'Location & Hours', 'Categories & Services', 'Photos & Details'];
  const businessName = formData.businessName || 'your business';
  const seoData      = {
    title:       `Add ${businessName} - ${stepNames[currentStep]} | Revfer`,
    description: `Creating your business profile on Revfer. Step ${currentStep + 1} of ${FORM_STEPS.length}.`
  };

  // ===== Handlers — all unchanged =====
  const uploadPhotoToS3 = async (file, presignedUrl) => {
    const response = await fetch(presignedUrl, {
      method:  'PUT',
      body:    file,
      headers: { 'Content-Type': file.type, 'x-amz-meta-eventtype': 'BusinessCreatePhotos' }
    });
    if (!response.ok) throw new Error('Failed to upload photo');
    return true;
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 0: {
        const basicErrors = validateBasicInformation(formData);
        setErrors(basicErrors);
        return Object.keys(basicErrors).length === 0;
      }
      case 1: {
        const locationErrors = validateLocationHours(formData);
        setErrors(locationErrors);
        return Object.keys(locationErrors).length === 0;
      }
      default: return true;
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNext = async () => {
    if (!canCreateBusiness) return;
    if (!validateCurrentStep()) return;

    if (currentStep === 0) {
      try {
        setCurrentStep(1);
        await triggerSearchBusinessName({
          payload: { auth: { token, authType: 'token' }, text: formData.businessName }
        }).unwrap();
      } catch {
        setCurrentStep(1);
      }
      return;
    }

    if (currentStep === FORM_STEPS.length - 1) {
      handleSubmit();
      return;
    }

    setCurrentStep(prev => prev + 1);
  };

  const handleBack = () => setCurrentStep(prev => prev - 1);

  const handleSubmit = async () => {
    if (!canCreateBusiness) return;

    const basicErrors    = validateBasicInformation(formData);
    const locationErrors = validateLocationHours(formData);
    const allErrors      = { ...basicErrors, ...locationErrors };

    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      setCurrentStep(Object.keys(basicErrors).length > 0 ? 0 : 1);
      return;
    }

    try {
      setIsLoading(true);

      let audioUrl = null, audioObjectKey;
      if (formData.audioDescription?.file && audioPresignedData?.url) {
        try {
          const audioKeyPath = await uploadAudioToS3(formData.audioDescription, audioPresignedData, userId);
          if (audioKeyPath) {
            audioUrl      = audioPresignedData.url.split('?')[0];
            audioObjectKey = audioKeyPath;
          }
        } catch {}
      }

      let photoUrl = null;
      if (formData.photo) {
        await uploadPhotoToS3(formData.photo, presignedUrlData.url);
        photoUrl = presignedUrlData.url.split('?')[0];
      }

      const businessData = {
        ...formData,
        photoUrl,
        audioUrl,
        audioObjectKey,
        audioMetadata: formData.audioDescription ? {
          duration: formData.audioDescription.duration,
          type:     formData.audioDescription.type,
          source:   formData.audioDescription.source,
          size:     formData.audioDescription.size
        } : null,
        photo:            undefined,
        audioDescription: undefined
      };

      setSubmitError('');
      const result = await addThing({
        auth: { token, authType: 'token' },
        spId,
        data: businessData
      }).unwrap();

      navigate(
        `/business/${result.businessId}/${(result.businessName || 'business').replace(/[\s,\.]+/g, '-').toLowerCase()}`
      );
    } catch (error) {
      console.error('Failed to add business:', error);
      // Server returns { message, errors: [...] } on 400 and { message } on 429.
      // Before this, a failed create showed nothing and owners retried then left.
      const body   = error?.data || {};
      const detail = Array.isArray(body.errors) && body.errors.length
        ? body.errors.join(' ')
        : (body.message || body.error || '');
      setSubmitError(
        detail
          ? `We couldn't create your business: ${detail}`
          : "We couldn't create your business. Please check your details and try again."
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0: return (
        <BasicInformationForm
          formData={formData}
          onChange={handleChange}
          isLoading={isLoading || isAudioUrlLoading}
          errors={errors}
          setErrors={setErrors}
          audioPresignedData={audioPresignedData}
          isAudioUrlLoading={isAudioUrlLoading}
          audioUrlError={audioUrlError}
        />
      );
      case 1: return (
        <LocationHoursForm
          formData={formData}
          onChange={handleChange}
          isLoading={isLoading}
          errors={errors}
        />
      );
      case 2: return (
        <CategoriesServicesForm
          formData={formData}
          onChange={handleChange}
          isLoading={isLoading}
        />
      );
      case 3: return (
        <PhotosDetailsForm
          formData={formData}
          onChange={handleChange}
          isLoading={isLoading}
        />
      );
      default: return null;
    }
  };

  if (isUserLoading) {
    return (
      <>
        <BasicSEO title="Loading Business Setup - Revfer" description="Loading your account information." noIndex={true} />
        <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Container>
      </>
    );
  }

  const sidebarProps = {
    userAccountType,
    currentBusinessCount,
    businessQuota,
    isUserLoading,
    currentStep
  };

  return (
    <>
      <BasicSEO title={seoData.title} description={seoData.description} noIndex={true} />

      {/* ===== Header ===== */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 2.5, mb: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={600}>
                Add a New Business
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5 }}>
                {stepNames[currentStep]} — Step {currentStep + 1} of {FORM_STEPS.length}
              </Typography>
            </Box>
            {isMobile && (
              <IconButton color="inherit" onClick={() => setSidebarOpen(true)}>
                <MenuIcon />
              </IconButton>
            )}
          </Box>

          {/* Progress bar in header */}
          <LinearProgress
            variant="determinate"
            value={((currentStep + 1) / FORM_STEPS.length) * 100}
            sx={{
              mt:          2,
              height:      6,
              borderRadius: 3,
              bgcolor:     'rgba(255,255,255,0.3)',
              '& .MuiLinearProgress-bar': { bgcolor: 'white', borderRadius: 3 }
            }}
          />
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ pb: 6 }}>

        {/* Submit error */}
        {submitError && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSubmitError('')}>
            {submitError}
          </Alert>
        )}

        {/* Upgrade prompt */}
        {!canCreateBusiness && (
          <UpgradePrompt
            userAccountType={userAccountType}
            currentBusinessCount={currentBusinessCount}
            businessQuota={businessQuota}
            onUpgrade={() => navigate('/premium')}
          />
        )}

        {isMobile ? (
          // ===== Mobile — single column =====
          <>
            <Paper
              variant="outlined"
              sx={{
                p:             3,
                border:        'none',
                borderRadius:  2,
                opacity:       !canCreateBusiness ? 0.5 : 1,
                pointerEvents: !canCreateBusiness ? 'none' : 'auto'
              }}
            >
              {renderStepContent(currentStep)}

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Button
                  onClick={handleBack}
                  disabled={currentStep === 0 || isLoading || !canCreateBusiness}
                  size="large"
                  variant="outlined"
                  sx={{ minWidth: 100 }}
                >
                  Back
                </Button>
                {currentStep >= 1 && currentStep < FORM_STEPS.length - 1 && (
                  <Button
                    variant="text"
                    onClick={handleSubmit}
                    disabled={isLoading || !canCreateBusiness}
                    size="large"
                  >
                    Create now
                  </Button>
                )}
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={isLoading || !canCreateBusiness}
                  size="large"
                  sx={{ minWidth: 140 }}
                >
                  {isLoading
                    ? <CircularProgress size={22} color="inherit" />
                    : currentStep === FORM_STEPS.length - 1
                      ? 'Submit'
                      : 'Next Step →'
                  }
                </Button>
              </Box>
            </Paper>

            <MobileDrawer
              isOpen={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
              {...sidebarProps}
            />
          </>
        ) : (
          // ===== Desktop — 2 columns: sidebar (3) + form (9) =====
          <Grid container spacing={4}>

            {/* Left — sidebar */}
            <Grid item xs={12} md={3}>
              <Box sx={{ position: 'sticky', top: 24 }}>
                <SidebarContent {...sidebarProps} />
              </Box>
            </Grid>

            {/* Right — form */}
            <Grid item xs={12} md={9}>
              <Paper
                variant="outlined"
                sx={{
                  p:             4,
                  border:        'none',
                  borderRadius:  2,
                  boxShadow:     '0 1px 4px rgba(0,0,0,0.06)',
                  opacity:       !canCreateBusiness ? 0.5 : 1,
                  pointerEvents: !canCreateBusiness ? 'none' : 'auto'
                }}
              >
                {renderStepContent(currentStep)}

                <Divider sx={{ my: 4 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button
                    onClick={handleBack}
                    disabled={currentStep === 0 || isLoading || !canCreateBusiness}
                    variant="outlined"
                    size="large"
                    sx={{ minWidth: 120 }}
                  >
                    ← Back
                  </Button>

                  {currentStep >= 1 && currentStep < FORM_STEPS.length - 1 ? (
                    <Button
                      variant="text"
                      onClick={handleSubmit}
                      disabled={isLoading || !canCreateBusiness}
                      size="large"
                    >
                      Skip the rest — create my business
                    </Button>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Step {currentStep + 1} of {FORM_STEPS.length}
                    </Typography>
                  )}

                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={isLoading || !canCreateBusiness}
                    size="large"
                    sx={{ minWidth: 160 }}
                  >
                    {isLoading
                      ? <CircularProgress size={22} color="inherit" />
                      : currentStep === FORM_STEPS.length - 1
                        ? 'Submit Business'
                        : 'Next Step →'
                    }
                  </Button>
                </Box>
              </Paper>
            </Grid>

          </Grid>
        )}
      </Container>
    </>
  );
};

export default AddBusinessPage;